import { useState } from 'react';
import { Page, Card, Layout, Text, BlockStack, InlineStack, TextField } from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { useShop } from '../hooks/useShop';
import { useCurrency } from '../hooks/useCurrency';
import { Endpoints } from '../api/endpoints';
import ExportButton from '../components/ExportButton';
import ForecastWidget from '../components/ForecastWidget';

interface ProfitReport {
  orders: number;
  revenue: number;
  cogs: number;
  shipping: number;
  tax: number;
  gateway_fee: number;
  ad_spend: number;
  returns: number;
  other_costs: number;
  gross_profit: number;
  net_profit: number;
  net_margin: number | null;
  operating_expenses: number;
  operating_profit: number;
  operating_margin: number | null;
}

const today = new Date().toISOString().slice(0, 10);
const thirtyDaysAgo = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);

export default function Reports() {
  const { shop } = useShop();
  const { formatMoney, formatPercent } = useCurrency(shop?.currency ?? 'USD');
  const [from, setFrom] = useState(thirtyDaysAgo);
  const [to, setTo] = useState(today);

  const report = useApi<ProfitReport>(Endpoints.reports.profit, { from, to });

  const lines: [string, number | undefined][] = [
    ['Revenue', report.data?.revenue],
    ['COGS', report.data?.cogs],
    ['Shipping', report.data?.shipping],
    ['Tax', report.data?.tax],
    ['Gateway fees', report.data?.gateway_fee],
    ['Ad spend', report.data?.ad_spend],
    ['Returns', report.data?.returns],
    ['Other costs', report.data?.other_costs],
  ];

  return (
    <Page
      title="Reports"
      subtitle="Profit & loss"
      secondaryActions={
        <ExportButton url={Endpoints.reports.export} params={{ from, to }} filename="profitvault-report.csv" />
      }
    >
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack gap="300">
                <TextField label="From" type="date" value={from} onChange={setFrom} autoComplete="off" />
                <TextField label="To" type="date" value={to} onChange={setTo} autoComplete="off" />
              </InlineStack>

              <BlockStack gap="200">
                {lines.map(([label, value]) => (
                  <InlineStack key={label} align="space-between">
                    <Text as="span" tone="subdued">
                      {label}
                    </Text>
                    <Text as="span">{formatMoney(value ?? 0)}</Text>
                  </InlineStack>
                ))}
                <InlineStack align="space-between">
                  <Text as="span" fontWeight="semibold">
                    Net profit (orders)
                  </Text>
                  <Text as="span" fontWeight="semibold">
                    {formatMoney(report.data?.net_profit ?? 0)} (
                    {formatPercent(report.data?.net_margin ?? 0)})
                  </Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">
                    − Operating expenses
                  </Text>
                  <Text as="span" tone="subdued">
                    {formatMoney(report.data?.operating_expenses ?? 0)}
                  </Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" fontWeight="bold">
                    Operating profit
                  </Text>
                  <Text as="span" fontWeight="bold">
                    {formatMoney(report.data?.operating_profit ?? 0)} (
                    {formatPercent(report.data?.operating_margin ?? 0)})
                  </Text>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <ForecastWidget />
        </Layout.Section>
      </Layout>
    </Page>
  );
}
